import type { ProjectEvent } from "@/engine/types";

/**
 * Reachable only via a Day-1 choice's `schedules`, never spontaneously —
 * eligibleWhen is `() => false` on purpose.
 */
export const act2Events: ProjectEvent[] = [
  {
    id: "eng_estimate_revision",
    title: "The First Crack",
    sender: "Security",
    day: "Day 3",
    eligibleWhen: () => false,
    body: [
      "Security emails: \"This pipeline touches PHI through a new AWS path. It needs a formal review — 5 to 7 business days, longer if there's a finding.\"",
      "You didn't budget for this. Your accepted estimate assumed engineering work was the whole project.",
      "Your director forwards the email: \"Didn't you already confirm four weeks?\"",
    ],
    choices: [
      {
        id: "reset_expectations",
        text: "Go back to the director and reset expectations now",
        description: "Explain the gap honestly before it compounds.",
        effects: [
          { metric: "stakeholderTrust", delta: -5 },
          { metric: "teamTrust", delta: 8 },
          { metric: "schedule", delta: 3 },
        ],
        resultText:
          "Uncomfortable conversation, but it's early — there's still time to replan. Director is frustrated but appreciates the early flag over a late surprise.",
      },
      {
        id: "absorb_silently",
        text: "Absorb the delay silently, push the team to make up time",
        description: "Say nothing yet, ask engineering to move faster instead.",
        effects: [
          { metric: "teamTrust", delta: -15 },
          { metric: "quality", delta: -10 },
          { metric: "schedule", delta: -8 },
        ],
        resultText:
          "Engineering feels squeezed for a slip they didn't cause. Corners start getting cut on error handling to protect the date. The gap doesn't close — it becomes invisible until UAT.",
        schedules: [{ eventId: "data_quality_issue", eligibleOnDay: 6 }],
      },
    ],
  },
  {
    id: "exec_escalation",
    title: "Defending the Eight Weeks",
    sender: "Director",
    day: "Day 2",
    eligibleWhen: () => false,
    body: [
      "Director: \"Leadership wants to know exactly why eight weeks. Walk me through it.\"",
      "You don't have a decomposition — just instinct that four weeks was tight. You're asked to justify a number you didn't actually build.",
    ],
    choices: [
      {
        id: "admit_and_redo",
        text: "Admit you haven't decomposed it yet, do it properly now",
        description: "Own the gap and go build the real estimate.",
        effects: [
          { metric: "stakeholderTrust", delta: -3 },
          { metric: "teamTrust", delta: 8 },
        ],
        resultText:
          "Director isn't happy about the redo, but respects the honesty over a defended guess. You go do the work you should have done on day one.",
        schedules: [{ eventId: "vendor_dependency_found", eligibleOnDay: 3 }],
      },
      {
        id: "improvise",
        text: "Improvise a justification to save face",
        description: "Reverse-engineer reasons for eight weeks on the spot.",
        effects: [
          { metric: "stakeholderTrust", delta: -15 },
          { metric: "teamTrust", delta: -5 },
        ],
        resultText:
          "The reasoning doesn't hold up under one follow-up question. Leadership now trusts neither the four-week nor the eight-week number — and neither do you.",
        schedules: [{ eventId: "scope_negotiation", eligibleOnDay: 4 }],
      },
    ],
  },
  {
    id: "vendor_dependency_found",
    title: "Decomposing the Work",
    sender: "Working Session",
    day: "Day 2",
    eligibleWhen: () => false,
    body: [
      "You pull together Data Engineering, BI, Security, Data Governance, and the business owner.",
      "Security: \"Any new AWS data path touching PHI needs a review. Typically 5–7 business days, longer with a finding.\"",
      "Data Governance: \"Source system access goes through a change advisory board. Next slot is 6 days out.\"",
      "Business owner: \"UAT needs real clinicians, and they're booked on two other go-lives this month.\"",
      "You now have real numbers: build (14d) + security review (~6d, partially parallel) + access provisioning (6d, blocking) + validation (3d) + UAT (5d) + deploy/hardening (2d) + defect buffer (3d).",
    ],
    choices: [
      {
        id: "realistic_estimate",
        text: "Present a realistic estimate with a critical path",
        description: "Show the director the dependency chain and why 4 weeks doesn't fit.",
        effects: [
          { metric: "stakeholderTrust", delta: 8 },
          { metric: "teamTrust", delta: 10 },
          { metric: "schedule", delta: 5 },
          { metric: "quality", delta: 5 },
        ],
        resultText:
          "Director isn't thrilled about the number but respects that it's backed by a real breakdown. Your estimate has teeth because it's traceable to actual constraints, not gut feel.",
      },
      {
        id: "compress_estimate",
        text: "Compress the estimate to look more palatable",
        description: "Present a shorter number knowing the real one is longer, plan to negotiate later.",
        effects: [
          { metric: "stakeholderTrust", delta: -8 },
          { metric: "teamTrust", delta: -10 },
          { metric: "quality", delta: -5 },
        ],
        resultText:
          "It buys short-term approval, but the moment security flags a finding or CAB slips a day, the plan visibly cracks — and now it looks like you hid the analysis rather than lacked it.",
      },
      {
        id: "fast_track",
        text: "Ask leadership to fast-track CAB and pre-clear security scope",
        description: "Negotiate the constraints themselves, not just the date.",
        effects: [
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "schedule", delta: 8 },
          { metric: "teamTrust", delta: 5 },
          { metric: "budget", delta: -5 },
        ],
        resultText:
          "Leadership pulls levers you can't — CAB agrees to an expedited review given executive sponsorship. It trims almost a week off the critical path, at the cost of a favor you'll need to justify later.",
      },
    ],
  },
  {
    id: "scope_negotiation",
    title: "The Scope Conversation",
    sender: "Director",
    day: "Day 3",
    eligibleWhen: () => false,
    body: [
      "Director: \"Leadership still wants a date. Given what you know now, what are we actually promising?\"",
      "You have a choice between reopening the date honestly, or quietly protecting it by absorbing the risk yourself.",
    ],
    choices: [
      {
        id: "renegotiate_date",
        text: "Renegotiate the date openly using what you've learned",
        description: "Trade a harder conversation now for a plan that can hold.",
        effects: [
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "schedule", delta: 8 },
          { metric: "budget", delta: -3 },
        ],
        resultText:
          "It's an awkward meeting, but leadership adjusts the go-live date and the plan underneath it stops being fiction.",
      },
      {
        id: "hold_the_line_silently",
        text: "Hold the original date, absorb the risk quietly",
        description: "Don't reopen the conversation — find the time internally.",
        effects: [
          { metric: "schedule", delta: -10 },
          { metric: "quality", delta: -8 },
          { metric: "teamTrust", delta: -8 },
        ],
        resultText:
          "The date holds on paper. Underneath it, the team is quietly cutting validation steps to protect a promise you made for them, not with them.",
      },
    ],
  },
];
